<template>
  <div id="header" class="header">
    <RouterLink to="/">
      <h1>대구공공시설예약서비스</h1>
    </RouterLink>
    <ul id="gnb">
      <li v-for="menu in menus" :key="menu.menuId">
        <a href="#" :data-menu-id="menu.menuId" :data-type-menu="menu.menuNm" @click.prevent="onClickTopMenu(menu)">
          {{ menu.menuNm }}
        </a>
      </li>
    </ul>
    <div class="right">
      <div id="sessionBox">
        <!-- 송은영 주무관 요청으로 테스트 텍스트 추가(2026-04-18) / 운영환경 미출력 -->
        <p v-if="!isProd" class="login_tit" style="font-size:15px; color: #a5a5a5; font-weight: bold; text-align: left; height:24px; line-height: 14px; padding: 4px; border-radius: 4px; border: 1px solid rgb(239, 75, 85); margin-right:10px;">테스트</p>
        로그아웃 남은 시간: <span id="session-timer">{{ sessionTimeDisplay }}</span>
        <button id="extendBtn" class="button sm darkgray" @click="extendSession">연장</button>
        <span v-if="sessionExtendMessage" class="session_feedback" role="status" aria-live="polite">
          {{ sessionExtendMessage }}
        </span>
      </div>
      <div class="header-util-bar">
        <Select
          id="inst"
          v-model="selectedInst"
          name="inst"
          title="기관 목록 선택"
          :items="instOptions"
        />
        <span class="admin">
          <strong><span class="name">{{ userName }}</span></strong> 님
        </span>
        <a href="#" class="btn n1" @click.prevent="logout" >로그아웃</a>
        <a href="#" class="btn n2" @click.prevent="router.push('/change-password')" >비밀번호변경</a>
      </div>
    </div>
  </div>
  <!-- 로그아웃 경고 팝업 -->
  <div class="pop_logout">
    <div class="inr">
      <p id="session_pop_info" class="tit">--:-- 후 자동 로그아웃됩니다.</p>
      <p class="txt">
        <span id="session_info_time">--:--</span> 동안 서비스를 이용하지 않아 <br />
        잠시 후 자동으로 로그아웃될 예정입니다. <br />
        로그인 시간을 연장하시겠습니까?
      </p>
    <div class="btn_area">
      <button type="button" class="button lg" @click="logout">로그아웃</button>
      <button type="button" class="button lg yellow" @click="extendSession">로그인 연장</button>
    </div>
    <p v-if="sessionExtendMessage" class="session_feedback popup" role="status" aria-live="polite">
      {{ sessionExtendMessage }}
    </p>
    <span class="close_popup" @click="closePopup"><span>닫기</span></span>
  </div>
</div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useApiMenu } from '@/composables/useApiMenu'
import { Select } from '@/components/form'
import { useMenuStore } from '@/store/menu.store'
import { useInstStore } from '@/store/inst.store'
import { customMemberLogout, getAccessTokenInfoCheck } from '@/service/auth.service'
import { getInstSysSetList } from  '@/service/cmsMng/configSetting/instSysSet.service'
import { useInstSysSetSession } from '@/composables/useInstSysSetSession'
import { countMenuAccess } from '@/service/stats.service'


// 사용자 정보 (토큰에서 가져오기)
const userName = ref('관리자')
const { saveInstSysSetList, clearInstSysSet } = useInstSysSetSession()

// 세션 타이머 관련
const remainingSeconds = ref(0)
let timerInterval = null
let sessionExpiredHandled = false
const isExtendingSession = ref(false)
const sessionExtendMessage = ref('')
let sessionExtendMessageTimer = null

// 세션 유효 시간 (분) - 필요 시 환경변수로 관리
const SESSION_DURATION_MIN = 30
const SESSION_TENANT = 'res-admin'
const SESSION_WARNED_KEY = 'sessionWarned'
const WARNING_SEC = 55
const SESSION_EXTEND_COOLDOWN_UNTIL_KEY = 'sessionExtendCooldownUntil'
const isProd = import.meta.env.PROD
const SESSION_EXTEND_FORCE_MIN_INTERVAL_SEC = (() => {
  const raw = Number(import.meta.env.VITE_SESSION_EXTEND_FORCE_MIN_INTERVAL_SEC ?? 15)
  if (!Number.isFinite(raw) || raw <= 0) return 15
  return Math.floor(raw)
})()
const SESSION_EXTEND_FEEDBACK_MODE = 'inline'

const getSessionExtendCooldownRemainSeconds = () => {
  const until = Number(localStorage.getItem(SESSION_EXTEND_COOLDOWN_UNTIL_KEY) || 0)
  if (!Number.isFinite(until) || until <= 0) return 0
  const remainMs = until - Date.now()
  if (remainMs <= 0) return 0
  return Math.ceil(remainMs / 1000)
}

const setSessionExtendCooldownSeconds = (seconds) => {
  const sec = Number(seconds)
  if (!Number.isFinite(sec) || sec <= 0) {
    localStorage.removeItem(SESSION_EXTEND_COOLDOWN_UNTIL_KEY)
    return
  }
  localStorage.setItem(SESSION_EXTEND_COOLDOWN_UNTIL_KEY, String(Date.now() + Math.floor(sec) * 1000))
}

const clearSessionExtendCooldown = () => {
  localStorage.removeItem(SESSION_EXTEND_COOLDOWN_UNTIL_KEY)
}

const showSessionExtendMessage = (message, ms = 1500) => {
  if (SESSION_EXTEND_FEEDBACK_MODE === 'alert') {
    alert(message)
    return
  }
  sessionExtendMessage.value = message
  if (sessionExtendMessageTimer) clearTimeout(sessionExtendMessageTimer)
  sessionExtendMessageTimer = setTimeout(() => {
    sessionExtendMessage.value = ''
  }, ms)
}

const extractRefreshApplied = (raw) => {
  if (!raw || typeof raw !== 'object') return null
  if (typeof raw.refreshApplied === 'boolean') return raw.refreshApplied
  if (typeof raw.refreshApplied === 'string') {
    const value = raw.refreshApplied.trim().toLowerCase()
    if (value === 'true' || value === 'y') return true
    if (value === 'false' || value === 'n') return false
  }
  if (raw.data && typeof raw.data === 'object' && typeof raw.data.refreshApplied === 'boolean') {
    return raw.data.refreshApplied
  }
  if (raw.data && typeof raw.data === 'object' && typeof raw.data.refreshApplied === 'string') {
    const value = raw.data.refreshApplied.trim().toLowerCase()
    if (value === 'true' || value === 'y') return true
    if (value === 'false' || value === 'n') return false
  }
  return null
}

const extractRefreshWaitSeconds = (raw) => {
  if (!raw || typeof raw !== 'object') return null
  const root = raw
  const data = raw.data && typeof raw.data === 'object' ? raw.data : {}
  const candidates = [
    root.guardRetryAfterSeconds,
    data.guardRetryAfterSeconds,
    root.refreshRetryAfterSeconds,
    data.refreshRetryAfterSeconds,
    root.retryAfterSeconds,
    data.retryAfterSeconds,
    root.refreshAvailableInSeconds,
    root.refreshCooldownSeconds,
    root.nextRefreshInSeconds,
    root.waitSeconds,
    data.refreshAvailableInSeconds,
    data.refreshCooldownSeconds,
    data.nextRefreshInSeconds,
    data.waitSeconds
  ]
  for (const v of candidates) {
    const n = Number(v)
    if (Number.isFinite(n) && n >= 0) return Math.floor(n)
  }
  return null
}

const extractResultCode = (raw) => {
  if (!raw || typeof raw !== 'object') return ''
  const code = raw.resultCode ?? raw?.data?.resultCode ?? ''
  return String(code || '').trim().toUpperCase()
}

const extractResultMessage = (raw) => {
  if (!raw || typeof raw !== 'object') return ''
  const message = raw.resultMessage ?? raw?.data?.resultMessage ?? raw.message ?? raw?.data?.message ?? ''
  return String(message || '').trim()
}

const extractRetryAfterSecondsFromMessage = (message) => {
  const text = String(message || '')
  if (!text) return null
  const match = text.match(/retryAfter\s*=\s*(\d+)\s*s?/i)
  if (!match) return null
  const sec = Number(match[1])
  return Number.isFinite(sec) && sec >= 0 ? Math.floor(sec) : null
}

/**
 * JWT 토큰 디코딩 (Base64URL → JSON)
 * @param {string} token - JWT 토큰
 * @returns {Object} 디코딩된 payload
 */
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return {}
  }
}

const sessionTimeDisplay = computed(() => {
  const min = Math.floor(remainingSeconds.value / 60)
  const sec = remainingSeconds.value % 60
  return `${min}:${sec.toString().padStart(2, '0')}`
})

function setSessionTimerByRemainingSeconds(remainingSeconds) {
  const sec = Number(remainingSeconds)
  if (!Number.isFinite(sec)) return false
  const safeSec = Math.max(0, Math.floor(sec))
  localStorage.setItem('sessionExpireAt', String(Date.now() + safeSec * 1000))
  localStorage.removeItem(SESSION_WARNED_KEY)
  return true
}

function closePopup() {
  document.querySelector('.pop_logout')?.classList.remove('on')
}

const formatMmss = (sec) => {
  const safeSec = Math.max(0, Math.floor(Number(sec) || 0))
  const min = Math.floor(safeSec / 60)
  const s = safeSec % 60
  return `${min}:${String(s).padStart(2, '0')}`
}

const updatePopupDisplay = (remainMs) => {
  const timeStr = formatMmss(remainMs / 1000)
  const infoEl = document.getElementById('session_pop_info')
  if (infoEl) infoEl.textContent = `${timeStr} 후 자동 로그아웃됩니다.`

  // 경과 시간 = 전체 세션 시간 - 남은 시간 ("mm:ss 동안 서비스를 이용하지 않아")
  const elapsedSec = Math.max(0, Math.floor((SESSION_DURATION_MIN * 60000 - Math.max(0, remainMs)) / 1000))
  const elapsedStr = formatMmss(elapsedSec)
  const infoTime = document.getElementById('session_info_time')
  if (infoTime) infoTime.textContent = elapsedStr
}

const showPopup = (remainMs) => {
  if (localStorage.getItem(SESSION_WARNED_KEY)) return
  localStorage.setItem(SESSION_WARNED_KEY, 'Y')
  updatePopupDisplay(remainMs)
  document.querySelector('.pop_logout')?.classList.add('on')
}

// 타이머 시작
function startSessionTimer() {
  // 기존 타이머 정리
  if (timerInterval) {
    clearInterval(timerInterval)
    timerInterval = null
  }
  
  // 남은 시간 계산 (초) - 매번 localStorage에서 최신 값 읽기
  const updateRemaining = () => {
    const expireAt = Number(localStorage.getItem('sessionExpireAt'))
    if (!expireAt || isNaN(expireAt)) {
      remainingSeconds.value = 0
      return
    }
    
    const now = Date.now()
    const diff = Math.max(0, Math.floor((expireAt - now) / 1000))
    remainingSeconds.value = diff
    updatePopupDisplay(diff * 1000)
    
    // 만료 시 로그아웃
    if (diff <= 0) {
      clearInterval(timerInterval)
      timerInterval = null
      handleSessionExpiredOnce()
      return
    }

    // warningSec 이하일 때 팝업 표시
    if (diff <= WARNING_SEC) {
      showPopup(diff * 1000)
    }
  }
  
  updateRemaining()
  timerInterval = setInterval(updateRemaining, 1000)
}

// 세션 연장 (API 없이 localStorage 갱신)
async function extendSession() {
  if (isExtendingSession.value) {
    showSessionExtendMessage('처리 중입니다.')
    return
  }
  const remainCooldown = getSessionExtendCooldownRemainSeconds()
  if (remainCooldown > 0) {
    showSessionExtendMessage(`${formatMmss(remainCooldown)} 후 강제 연장 가능`)
    return
  }
  isExtendingSession.value = true
  const accessToken = localStorage.getItem('accessToken')
  if (!accessToken) {
    showSessionExtendMessage('로그인 정보가 없어 연장할 수 없습니다.')
    isExtendingSession.value = false
    return
  }

  try {
    const info = await getAccessTokenInfoCheck({
      accessToken,
      refreshMode: 'FORCE',
      tenant: SESSION_TENANT
    })
    if (isDuplicateLoginInfo(info?.raw)) {
      handleDuplicateLoginOnce()
      return
    }
    const remainSeconds = info?.remainingSeconds ?? null
    const refreshApplied = extractRefreshApplied(info?.raw)
    const refreshWaitSeconds = extractRefreshWaitSeconds(info?.raw)
    const resultCode = extractResultCode(info?.raw)
    const resultMessage = extractResultMessage(info?.raw)
    const resultMessageUpper = resultMessage.toUpperCase()
    const retryAfterFromMessage = extractRetryAfterSecondsFromMessage(resultMessage)
    const isCooldownCode =
      resultCode.includes('COOLDOWN') ||
      resultCode.includes('LOCK') ||
      resultCode.includes('FORCE_REFRESH_LOCKED') ||
      resultCode.includes('TOO_MANY') ||
      resultCode.includes('RETRY')
    const isCooldownMessage =
      resultMessageUpper.includes('COOLDOWN') ||
      resultMessageUpper.includes('LOCK') ||
      resultMessageUpper.includes('FORCE_REFRESH_LOCKED') ||
      resultMessageUpper.includes('TOO_MANY') ||
      resultMessageUpper.includes('RETRY')
    const isForceApplied = refreshApplied === true
    const hasGuardSignal = isCooldownCode || isCooldownMessage
    const cooldownSeconds = Number.isFinite(refreshWaitSeconds)
      ? refreshWaitSeconds
      : retryAfterFromMessage
    const shouldRetry =
      !isForceApplied && (hasGuardSignal || Number.isFinite(cooldownSeconds))

    if (remainSeconds === 0) {
      handleSessionExpiredOnce()
      return
    }

    if (remainSeconds !== null) {
      if (isForceApplied) {
        const nextCooldown = Number.isFinite(cooldownSeconds) && cooldownSeconds > 0
          ? cooldownSeconds
          : SESSION_EXTEND_FORCE_MIN_INTERVAL_SEC
        if (nextCooldown > 0) setSessionExtendCooldownSeconds(nextCooldown)
        else clearSessionExtendCooldown()
      } else if (Number.isFinite(cooldownSeconds) && cooldownSeconds > 0) {
        setSessionExtendCooldownSeconds(cooldownSeconds)
      }
      setSessionTimerByRemainingSeconds(remainSeconds)
      startSessionTimer()
      if (isForceApplied) {
        const successCooldown = getSessionExtendCooldownRemainSeconds()
        if (successCooldown > 0) showSessionExtendMessage(`${formatMmss(successCooldown)} 후 강제 연장 가능`)
        else showSessionExtendMessage('연장되었습니다.')
      } else if (shouldRetry) {
        showSessionExtendMessage('잠시 후 재요청 바랍니다.')
      } else {
        showSessionExtendMessage('세션 연장에 실패했습니다. 잠시 후 재시도해 주세요.')
      }
    } else {
      showSessionExtendMessage(shouldRetry ? '잠시 후 재요청 바랍니다.' : '세션 연장에 실패했습니다. 잠시 후 재시도해 주세요.')
    }
  } catch (e) {
    if (isUnauthorizedError(e)) {
      handleSessionExpiredOnce()
      return
    }
    showSessionExtendMessage('세션 연장 중 오류가 발생했습니다. 잠시 후 재시도해 주세요.')
  } finally {
    isExtendingSession.value = false
    closePopup()
  }
}

const autoRefreshSession = async () => {
  const accessToken = localStorage.getItem('accessToken')
  if (!accessToken) return

  try {
    const info = await getAccessTokenInfoCheck({
      accessToken,
      refreshMode: 'AUTO',
      tenant: SESSION_TENANT
    })
    if (isDuplicateLoginInfo(info?.raw)) {
      handleDuplicateLoginOnce()
      return
    }
    const remainSeconds = info?.remainingSeconds ?? null
    if (remainSeconds === 0) {
      handleSessionExpiredOnce()
      return
    }
    if (remainSeconds !== null) {
      setSessionTimerByRemainingSeconds(remainSeconds)
      startSessionTimer()
    }
  } catch (e) {
    if (isUnauthorizedError(e)) {
      handleSessionExpiredOnce()
    }
  }
}

onMounted(async () => {
  // 세션 타이머 시작
  startSessionTimer()
  autoRefreshSession()
  
  // 사용자명 설정 (토큰에서 추출)
  try {
    const token = localStorage.getItem('accessToken')
    if (token) {
      const payload = parseJwt(token)
      userName.value = payload.name || payload.mbrName || payload.sub || '관리자'
    }
  } catch {
    // 토큰 파싱 오류 무시
  }
  
  // 기관 목록 로드 (loadInstList 내에서 localStorage 동기화 처리됨)
  await instStore.loadInstList()
})

// 컴포넌트 언마운트 시 타이머 정리
onUnmounted(() => {
  if (timerInterval) {
    clearInterval(timerInterval)
  }
  if (sessionExtendMessageTimer) {
    clearTimeout(sessionExtendMessageTimer)
  }
})

const clearAuthStorage = () => {
  // localStorage 정리 (모든 인증 관련 데이터)
  localStorage.removeItem('accessToken')
  localStorage.removeItem('idToken')
  localStorage.removeItem('SELECTED_INST')
  localStorage.removeItem('SELECTED_INST_NM')
  localStorage.removeItem('sessionExpireAt')
  localStorage.removeItem(SESSION_WARNED_KEY)
  localStorage.removeItem(SESSION_EXTEND_COOLDOWN_UNTIL_KEY)
  localStorage.removeItem('userInfo')
  localStorage.removeItem('code_verifier')
  // Pinia 상태도 초기화
  instStore.$patch({
    instOptions: [],
    selectedInst: '',
    selectedInstNm: '',
    changeSeq: 0
  })
}

const forceLogout = () => {
  clearAuthStorage()
  window.location.href = window.location.origin + '/login'
}

const isUnauthorizedError = (error) => {
  const status = error?.response?.status
  return status === 401 || status === 403
}

const isDuplicateLoginInfo = (info) => {
  if (!info || typeof info !== 'object') return false
  const raw = info.raw || info
  const reason = String(raw.logoutReason ?? raw?.data?.logoutReason ?? '').toUpperCase()
  const code = String(raw.resultCode ?? raw?.data?.resultCode ?? '').toUpperCase()
  return reason === 'DUPLICATE_LOGIN' || code.includes('DUPLICATE_LOGIN')
}

const handleSessionExpiredOnce = (message) => {
  if (sessionExpiredHandled) return
  sessionExpiredHandled = true
  alert(message || '세션이 만료되었습니다. 다시 로그인해 주세요.')
  forceLogout()
}

const handleDuplicateLoginOnce = () => {
  handleSessionExpiredOnce('다른 로그인이 감지되어 로그아웃되었습니다. 다시 로그인해 주세요.')
}

// 로그아웃 구현
async function logout() {
  try {
    await customMemberLogout()
  } catch {
    alert('로그아웃 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
  } finally {
    clearAuthStorage()
    window.location.href = window.location.origin + '/login'
  }
}

defineProps({
  menus: {
    type: Array,
    default: () => []
  },
  selectedMenuId: {
    type: String,
    default: null
  },
  currentMenu: {
    type: Object,
    default: null
  }
})

const menuStore = useMenuStore()
const instStore = useInstStore()

// v-model용 computed (set에서 changeInst 호출)
const selectedInst = computed({
  get: () => instStore.selectedInst,
  set: (v) => instStore.changeInst(v)
})

const instOptions = computed(() => instStore.instOptions)


const emit = defineEmits(['menu-change'])

const router = useRouter()
const route = useRoute()
const { menusWithLevel } = useApiMenu()

/**
 * DIR 타입 메뉴인 경우, 실제 menuLink가 있는 첫 번째 하위 메뉴 찾기
 */
function findFirstActualMenuLink(menuId) {
  const menus = menusWithLevel.value || []

  const findMenu = (id) => menus.find(m => m.menuId === id)

  function search(id) {
    const menu = findMenu(id)
    if (!menu) return null

    // 실제 이동은 path 우선
    const realPath =
      (menu.path && menu.path !== 'N' && menu.path !== 'dir') ? menu.path :
      (menu.menuLink && menu.menuLink !== 'N' && menu.menuLink !== 'dir') ? menu.menuLink :
      null

    if (realPath) return realPath

    if (menu.menuType === 'DIR') {
      const children = menus.filter(m => m.upMenuId === id)
      for (const child of children) {
        const result = search(child.menuId)
        if (result) return result
      }
    }
    return null
  }

  return search(menuId)
}

/**
 * DIR 타입 메뉴에서 실제 이동할 첫 번째 하위 메뉴 객체 찾기
 */
function findFirstActualMenu(menuId) {
  const menus = menusWithLevel.value || []
  const findMenu = (id) => menus.find(m => m.menuId === id)

  function search(id) {
    const menu = findMenu(id)
    if (!menu) return null
    const realPath =
      (menu.path && menu.path !== 'N' && menu.path !== 'dir') ? menu.path :
      (menu.menuLink && menu.menuLink !== 'N' && menu.menuLink !== 'dir') ? menu.menuLink :
      null
    if (realPath) return menu
    if (menu.menuType === 'DIR') {
      const children = menus.filter(m => m.upMenuId === id)
      for (const child of children) {
        const result = search(child.menuId)
        if (result) return result
      }
    }
    return null
  }
  return search(menuId)
}


const onClickTopMenu = (menu) => {
  // 1) 전역에 현재 선택된 메뉴 저장
  menuStore.setCurrentMenu(menu.menuId)

  // 2) 부모에 알리기
  emit('menu-change', menu.menuId)

  // 3) 라우팅 로직: DIR이면 첫 번째 실제 페이지로, 아니면 자신의 menuLink로
  let targetLink = null
  
  if (menu.menuType === 'DIR') {
    targetLink = findFirstActualMenuLink(menu.menuId)
  } else {
    targetLink =
      (menu.path && menu.path !== 'N') ? menu.path :
      (menu.menuLink && menu.menuLink !== 'N') ? menu.menuLink :
      null
  }

  if (targetLink) {
    // path(매핑된 실제 경로) 우선 사용
    const link = menu.path || targetLink

    // 메뉴 접속수 카운트 (DIR이면 실제 도달하는 하위 메뉴 기준)
    const actualMenu = menu.menuType === 'DIR' ? findFirstActualMenu(menu.menuId) : menu
    if (actualMenu?.menuId) {
      countMenuAccess({ menuUrl: link, menuId: actualMenu.menuId }).catch(() => {})
    }

    // 레거시 형식 "/board-mng/board/list/:bbsId" → 쿼리 기반으로 변환
    const legacyBbsMatch = link.match(/^(.*\/board-mng\/board\/list)\/([^/?#]+)$/)
    const normalizedLink = legacyBbsMatch
      ? `${legacyBbsMatch[1]}/${legacyBbsMatch[2]}`
      : link

    const [pathPart, queryPart = ''] = normalizedLink.split('?')
    const linkQuery = Object.fromEntries(new URLSearchParams(queryPart).entries())

    // 쿼리는 초기값(bbsId)만 유지하고 나머지는 리셋
    const nextQuery = {
      ...(linkQuery.bbsId ? { bbsId: linkQuery.bbsId } : {}),
      menuId: menu.menuId
    }

    router.push({
      path: pathPart,
      query: nextQuery
    })
  }
}

const fetchInstSysSetList = async (targetInstId = null) => {
  const instId = targetInstId || localStorage.getItem('SELECTED_INST')
  if (!instId) return
  // 기관 변경 시 이전 기관 캐시는 유지하지 않고 비운다.
  clearInstSysSet()
  window.dispatchEvent(new CustomEvent('inst-sys-set-updated'))

  const payload = {
    searchInstId: instId,
    searchUseYn: 'Y'
  }
  const res = await getInstSysSetList(payload)
  if (res?.result) {
    saveInstSysSetList(instId, res)
    window.dispatchEvent(new CustomEvent('inst-sys-set-updated'))
  }
}

watch(
  () => instStore.selectedInst,
  (newInstId) => {
    if (!newInstId) return
    fetchInstSysSetList(newInstId)
  },
  { immediate: true }
)

const handleStorageChange = (event) => {
  if (event.key !== 'SELECTED_INST') return
  const nextInstId = event.newValue || localStorage.getItem('SELECTED_INST')
  if (!nextInstId) return
  fetchInstSysSetList(nextInstId)
}

onMounted(() => {
  window.addEventListener('storage', handleStorageChange)
})

watch(
  () => route.fullPath,
  () => {
    autoRefreshSession()
  }
)

onUnmounted(() => {
  window.removeEventListener('storage', handleStorageChange)
})
</script>
<style scoped>
  :deep(.form-select){
    max-width: 200px;
  }
</style>
